import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const GalleryCard = ({ gallery, onShare, onEdit, onViewAnalytics }) => {
  const navigate = useNavigate();
  const [imageLoaded, setImageLoaded] = useState(false);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const handleCardClick = () => {
    navigate('/gallery-management', { state: { galleryId: gallery?.id } });
  };

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden smooth-transition hover-lift shadow-warm-sm">
      <div 
        className="relative h-48 md:h-56 lg:h-64 overflow-hidden cursor-pointer group"
        onClick={handleCardClick}
      >
        <Image
          src={gallery?.thumbnail}
          alt={gallery?.thumbnailAlt}
          className={`w-full h-full object-cover smooth-transition group-hover:scale-105 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setImageLoaded(true)}
        />
        {!imageLoaded && (
          <div className="absolute inset-0 bg-muted animate-pulse" />
        )}
        
        <div className="absolute top-3 right-3 flex gap-2">
          {gallery?.isActive && (
            <span className="bg-success text-success-foreground px-2 py-1 rounded-md text-xs font-medium">
              Active
            </span>
          )}
          {gallery?.syncStatus === 'syncing' && (
            <span className="bg-warning text-warning-foreground px-2 py-1 rounded-md text-xs font-medium flex items-center gap-1">
              <Icon name="RefreshCw" size={12} className="animate-spin" />
              Syncing
            </span>
          )}
        </div>

        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 smooth-transition flex items-end p-4">
          <div className="flex items-center gap-2 text-white">
            <Icon name="Eye" size={16} />
            <span className="text-sm">View Gallery</span>
          </div>
        </div>
      </div>
      <div className="p-4 md:p-5">
        <div className="mb-3">
          <h3 className="text-lg font-semibold mb-1 line-clamp-1">{gallery?.name}</h3>
          <p className="text-sm text-muted-foreground line-clamp-2">{gallery?.description}</p>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4 pb-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Icon name="Images" size={16} color="var(--color-muted-foreground)" />
            <div>
              <p className="text-xs text-muted-foreground">Media Files</p>
              <p className="text-sm font-medium data-text">{gallery?.mediaCount}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="Users" size={16} color="var(--color-muted-foreground)" />
            <div>
              <p className="text-xs text-muted-foreground">Visitors</p>
              <p className="text-sm font-medium data-text">{gallery?.visitorCount}</p>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Icon name="Calendar" size={14} />
            <span>Created {formatDate(gallery?.createdDate)}</span>
          </div>
          {gallery?.lastAccessed && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Icon name="Clock" size={14} />
              <span>{formatDate(gallery?.lastAccessed)}</span>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            iconName="Share2"
            iconPosition="left"
            onClick={(e) => {
              e?.stopPropagation();
              onShare(gallery);
            }}
            className="flex-1 min-w-[100px]"
          >
            Share
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="Edit"
            iconPosition="left"
            onClick={(e) => {
              e?.stopPropagation();
              onEdit(gallery);
            }}
            className="flex-1 min-w-[100px]"
          >
            Edit
          </Button>
          <Button
            variant="ghost"
            size="sm"
            iconName="BarChart3"
            onClick={(e) => {
              e?.stopPropagation();
              onViewAnalytics(gallery);
            }}
            className="px-3"
          >
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GalleryCard;